import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import CronConfigDialog from '../CronConfigDialog';
import {useSnackbar} from '../../../contexts/SnackbarContext';
import {ActionButton, CancelButton} from '../../common/ActionButtons';
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import FieldHelpSuffix from '../../common/FieldHelpSuffix';
import {jobsService} from "../../../services/jobs.service";
import {collectionService} from "../../../services/collection.service";
import {type DataSource} from "../../../models/models";
import {JobFormState, JobParamsRequest, JobType, StorageLocation} from "../../../models/jobs.models";
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import CreateDataSourceDialog from "../../data-sources/CreateDataSourceDialog";
import {useFeatureConfig} from "../../../contexts/FeatureConfigContext";
import {FEATURE_KEYS} from "../../../models/admin-config.models";

interface CreateUpdateSchedulerDialogPropsType {
    open: boolean;
    onClose: () => void;
    onJobCreated: () => void;
}

const INITIAL_FORM_DATA = {
    cronExpression: '0 0 12 * * ?',
    targetCollectionName: '',
    targetCollectionId: '',
    sourceCollectionId: '',
    inputPath: '',
    outputPath: '',
    prompt: '',
} as JobFormState;

const validateForm = (
    formData: JobFormState,
    isChecked: boolean,
    sourceLocation: StorageLocation,
    targetLocation: StorageLocation,
): boolean => {
    const {inputPath, outputPath, sourceCollectionId, targetCollectionId, targetCollectionName, cronExpression, prompt} = formData;

    const isInputValid = sourceLocation === StorageLocation.SERVER
        ? Boolean(inputPath.trim()) && sourceCollectionId === ''
        : Boolean(sourceCollectionId) && !inputPath.trim();

    const isOutputValid = targetLocation === StorageLocation.SERVER
        ? Boolean(outputPath.trim()) && targetCollectionId === '' && !targetCollectionName.trim()
        : Boolean(targetCollectionId) && !outputPath.trim();

    const isPromptValid = isChecked ? true : Boolean(prompt.trim());

    return isInputValid && isOutputValid && Boolean(cronExpression.trim()) && isPromptValid;
};

type DialogState =
    | { type: 'create' }
    | { type: 'cron'; job: JobFormState }
    | null;

const CreateUpdateSchedulerDialog = ({open, onClose, onJobCreated}: CreateUpdateSchedulerDialogPropsType) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const {isFeatureEnabled} = useFeatureConfig();

    const isSharePointEnabled = isFeatureEnabled(FEATURE_KEYS.SHAREPOINT_KEY);

    const [checked, setChecked] = useState(false);
    const [formData, setFormData] = useState<JobFormState>(INITIAL_FORM_DATA);

    const [collections, setCollections] = useState<DataSource[]>([]);
    const [sourceLocation, setSourceLocation] = useState<StorageLocation>(StorageLocation.SERVER);
    const [targetLocation, setTargetLocation] = useState<StorageLocation>(StorageLocation.SERVER);

    const [loading, setLoading] = useState(false);

    const [openDialog, setOpenDialog] = useState<DialogState>(null);

    const isFormValid = validateForm(formData, checked, sourceLocation, targetLocation);

    const handleFetchCollections = useCallback(async () => {
        collectionService.invalidateCache();

        try {
            const availableCollections = await collectionService.fetchCollections();
            setCollections(availableCollections?.content ?? []);
        } catch (err) {
            console.error("Error fetching collections:", err);
            showSnackbar(parseError(err), "error");
        }
    }, [showSnackbar]);

    useEffect(() => {
        void handleFetchCollections();
    }, []);

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isSharePointEnabled) {
            setChecked(false);
            return;
        }

        setChecked(event.target.checked);
        setSourceLocation(event.target.checked ? StorageLocation.SERVER : StorageLocation.COLLECTION);
        setTargetLocation(event.target.checked ? StorageLocation.COLLECTION : StorageLocation.SERVER);
        setFormData(prev => ({
            ...prev,
            sourceCollectionId: '',
            targetCollectionId: '',
            inputPath: '',
            outputPath: '',
        }));
    };

    const handleFormChange = (field: keyof typeof formData, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleToggleLocation = (data: 'source' | 'target', location: StorageLocation) => {
        if (data === 'source') {
            setSourceLocation(location);

            if (location === StorageLocation.SERVER) {
                handleFormChange('sourceCollectionId', '');
            } else {
                handleFormChange('inputPath', '');
            }
        } else {
            setTargetLocation(location);

            if (location === StorageLocation.SERVER) {
                handleFormChange('targetCollectionId', '');
                handleFormChange('targetCollectionName', '');
            } else {
                handleFormChange('outputPath', '');
            }
        }
    };

    const handleSubmit = async () => {
        if (!isFormValid) {
            showSnackbar(t('createUpdateScheduler.allFieldsRequired'), "error");
            return;
        }
        setLoading(true);

        const {inputPath, outputPath, sourceCollectionId, targetCollectionId, cronExpression, prompt} = formData;

        const params: JobParamsRequest = {
            cronExpression,
            prompt,
            ...(sourceLocation === StorageLocation.SERVER
                ? { inputPath }
                : { sourceCollectionId: sourceCollectionId || undefined }),
            ...(targetLocation === StorageLocation.SERVER
                ? { outputPath }
                : { targetCollectionId: targetCollectionId || undefined }),
        };

        const sharePointParams: JobParamsRequest = {
            type: JobType.SHAREPOINT,
            cronExpression,
            inputSharepointPath: inputPath,
            targetCollectionId: targetCollectionId || undefined,
        }

        try {
            await jobsService.createJob(checked ? sharePointParams : params);

            showSnackbar(t('createUpdateScheduler.updateAddedSuccess'), 'success');
            onJobCreated();
            setFormData(INITIAL_FORM_DATA);
            onClose();
        } catch (err) {
            showSnackbar(parseError(err), "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setFormData(INITIAL_FORM_DATA);
        setLoading(false);
        onClose();
    }

    const handleSaveCron = (newCron: string) => {
        handleFormChange('cronExpression', newCron);
    };

    const handleDataSourceCreated = async (newId: number) => {
        await handleFetchCollections();
        handleFormChange('targetCollectionId', newId);
    };

    return (
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle sx={{padding: '24px 32px 16px'}}>
                {t('createUpdateScheduler.title')}
            </DialogTitle>

            <DialogContent sx={{padding: '16px 32px'}}>
                {isSharePointEnabled && (
                    <FormControlLabel
                        control={<Switch checked={checked} onChange={handleSwitchChange} />}
                        label={t('createUpdateScheduler.switchLabel')}
                        sx={{ mt: 2 }}
                    />
                )}

                <Grid container spacing={2} alignItems="flex-start" sx={{mt: 1}}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3} direction='column'>
                            <Typography variant="subtitle1">
                                {t('createUpdateScheduler.sourceData')}
                            </Typography>

                            <ToggleButtonGroup value={sourceLocation} exclusive fullWidth
                                               onChange={(e, value) => value && handleToggleLocation('source', value)}>
                                <ToggleButton value={StorageLocation.SERVER}>
                                    {t('createUpdateScheduler.server')}
                                </ToggleButton>
                                <ToggleButton value={StorageLocation.COLLECTION} disabled={checked}>
                                    {t('createUpdateScheduler.collection')}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {sourceLocation === StorageLocation.SERVER ?
                                (<TextField
                                    label={t('createUpdateScheduler.inputPath')}
                                    fullWidth
                                    value={formData.inputPath}
                                    onChange={(e) => handleFormChange('inputPath', e.target.value)}
                                    required
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title={t('createUpdateScheduler.help.inputPath')}>
                                                <InputIcon aria-hidden="true" sx={{width: '20px', height: '20px', color: 'text.secondary' }} />
                                            </Tooltip>
                                        ),
                                    }}
                                />)
                                : (<FormControl fullWidth>
                                    <InputLabel required id="source-collection-select-label">{t('createUpdateScheduler.source')}</InputLabel>

                                    <Select
                                        labelId="source-collection-select-label"
                                        value={formData.sourceCollectionId}
                                        label={t('createUpdateScheduler.source')}
                                        required
                                        onChange={(e) => handleFormChange('sourceCollectionId', e.target.value as DataSource['id'])}
                                        sx={{
                                            borderRadius: '16px',
                                            '& .MuiSelect-icon': {
                                                right: 40,
                                            },
                                        }}
                                        input={
                                            <OutlinedInput
                                                label={t('createUpdateScheduler.source')}
                                                endAdornment={
                                                    <FieldHelpSuffix
                                                        asAdornment
                                                        title={t('createUpdateScheduler.help.source')}
                                                        ariaLabel={t('createUpdateScheduler.help.source') as string}
                                                    />
                                                }
                                                sx={{ pr: '72px' }}
                                            />
                                        }
                                    >
                                        {collections.length > 0 && (
                                            collections.map(source => (
                                                <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>)
                            }
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Stack spacing={3} direction='column'>
                            <Typography variant="subtitle1">
                                {t('createUpdateScheduler.targetData')}
                            </Typography>

                            <ToggleButtonGroup value={targetLocation} exclusive fullWidth
                                               onChange={(e, value) => value && handleToggleLocation('target', value)}>
                                <ToggleButton value={StorageLocation.SERVER} disabled={checked}>
                                    {t('createUpdateScheduler.server')}
                                </ToggleButton>
                                <ToggleButton value={StorageLocation.COLLECTION}>
                                    {t('createUpdateScheduler.collection')}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {targetLocation === StorageLocation.SERVER ?
                                (<TextField
                                    label={t('createUpdateScheduler.outputPath')}
                                    fullWidth
                                    value={formData.outputPath}
                                    onChange={(e) => handleFormChange('outputPath', e.target.value)}
                                    required
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title={t('createUpdateScheduler.help.outputPath')}>
                                                <OutputIcon aria-hidden="true"
                                                            sx={{width: '20px', height: '20px', color: 'text.secondary'}}/>
                                            </Tooltip>
                                        ),
                                    }}
                                />)
                                : (<FormControl fullWidth>
                                        <InputLabel required id="target-collection-select-label">{t('createUpdateScheduler.target')}</InputLabel>

                                        <Select
                                            labelId="target-collection-select-label"
                                            value={formData.targetCollectionId}
                                            label={t('createUpdateScheduler.target')}
                                            required
                                            onChange={(e) => {
                                                if (e.target.value === '__add_new__') return;
                                                handleFormChange('targetCollectionId', e.target.value as DataSource['id']);
                                            }}
                                            sx={{
                                                borderRadius: '16px',
                                                '& .MuiSelect-icon': {
                                                    right: 40,
                                                },
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t('createUpdateScheduler.target')}
                                                    endAdornment={
                                                        <FieldHelpSuffix
                                                            asAdornment
                                                            title={t('createUpdateScheduler.help.target')}
                                                            ariaLabel={t('createUpdateScheduler.help.target') as string}
                                                        />
                                                    }
                                                    sx={{ pr: '72px' }}
                                                />
                                            }
                                        >
                                            <MenuItem value="__add_new__" onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setOpenDialog({type: 'create'});
                                            }}>
                                                {t('createUpdateScheduler.addNewDataSource')}
                                            </MenuItem>

                                            {collections.length > 0 && (
                                                collections.map(target => (
                                                    <MenuItem key={target.id} value={target.id}>{target.name}</MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                )
                            }
                        </Stack>
                    </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="center" sx={{mt: 3}}>
                    <Grid item xs>
                        <TextField
                            label={t('createUpdateScheduler.cronExpression')}
                            fullWidth
                            value={formData.cronExpression}
                            onChange={(e) => handleSaveCron(e.target.value)}
                            required
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('createUpdateScheduler.help.cronExpression')}
                                        ariaLabel={t('createUpdateScheduler.help.cronExpression') as string}
                                    />
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={() => setOpenDialog({type: 'cron', job: formData})}
                            sx={{height: '56px', width: '100px'}}
                        >
                            {t('createUpdateScheduler.configure')}
                        </Button>
                    </Grid>
                </Grid>

                <TextField
                    key='prompt'
                    disabled={checked}
                    label={t('createUpdateScheduler.prompt')}
                    fullWidth
                    multiline
                    rows={5}
                    value={formData['prompt']}
                    onChange={(e) => handleFormChange('prompt', e.target.value)}
                    required
                    sx={{mt: 3}}
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix asAdornment
                                             title={t('createUpdateScheduler.help.prompt')}
                                             ariaLabel={t('createUpdateScheduler.help.prompt') as string}
                            />
                        ),
                    }}
                />
            </DialogContent>

            <DialogActions sx={{padding: '16px 32px 24px', justifyContent: 'flex-end', gap: '8px'}}>
                <CancelButton onClick={handleCloseDialog}>
                    {t('common.cancel')}
                </CancelButton>

                <Box sx={{position: 'relative'}}>
                    <ActionButton
                        onClick={handleSubmit}
                        disabled={loading || !isFormValid}
                    >
                        {t('createUpdateScheduler.create')}
                    </ActionButton>

                    {loading && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: 'primary.main',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Box>
            </DialogActions>

            {openDialog?.type === 'cron' && (
                <CronConfigDialog
                    open
                    currentCron={formData.cronExpression}
                    onClose={() => setOpenDialog(null)}
                    onSave={handleSaveCron}
                    mode="create"
                />
            )}

            {openDialog?.type === 'create' && (
                <CreateDataSourceDialog
                    open
                    onClose={() => setOpenDialog(null)}
                    onDataSourceCreated={handleDataSourceCreated}
                />
            )}
        </Dialog>
    );
};

export default CreateUpdateSchedulerDialog;
